import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TUI_IS_IOS, TUI_IS_MOBILE } from '@taiga-ui/cdk';
import { Amplify, Auth, I18n } from 'aws-amplify';
import { distinctUntilChanged, filter, Subscription } from 'rxjs';
import { AppInitService } from './app-init.factory';
import awsExports from 'src/aws-exports';
import { AuthService } from './services/auth.service';
import { BalanceService } from './services/balance.service';
import { EsimService } from './features/esim/esim.service';
import { GlobalDataService } from './services/global-data.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private balanceService = inject(BalanceService);
  private esimService = inject(EsimService);
  private globalDataService = inject(GlobalDataService);
  private appInitService = inject(AppInitService);
  isLoggedIn$ = this.authService.isLoggedIn$;
  initialized$ = this.authService.initialized$;
  title = 'skylink-fullstack-code';
  amplify = {
    es: {
      'Sign In': 'Registrarse',
      'Sign Up': 'Regístrate',
    },
  };

  //keep refs to subscriptions to be able to unsubscribe later

  private subscription = new Subscription();

  @ViewChild('drawer') drawer!: MatDrawer;

  constructor(
    private translate: TranslateService,
    @Inject(TUI_IS_MOBILE) readonly isMobile: boolean,
    @Inject(TUI_IS_IOS) readonly isIos: boolean,
    private router: Router
  ) {
    Amplify.configure(awsExports);
    I18n.putVocabularies(this.amplify);
  }

  async ngOnInit() {
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('en');
    const localeLanguage = window.localStorage.getItem('_language');
    // the lang to use, if the lang isn't available, it will use the current loader to get them
    let language = this.translate.getBrowserLang();
    if (localeLanguage && localeLanguage !== language) {
      language = localeLanguage;
      window.localStorage.setItem('_language', localeLanguage);
      this.translate.use(localeLanguage);
    } else {
      window.localStorage.setItem('_language', language ?? 'en');
      this.translate.use(language ?? 'en');
    }
    
    this.subscription.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.drawer?.close();
          this.refreshStaleData();
        }
      })
    );

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.refreshStaleData();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    this.subscription.add({ unsubscribe: () => document.removeEventListener('visibilitychange', onVisibilityChange) });

    // Carga en background del balance al autenticarse
    this.subscription.add(
      this.authService.authStatus$.pipe(
        distinctUntilChanged((a, b) => a.loggedIn === b.loggedIn),
        filter(status => status.loggedIn)
      ).subscribe(() => {
        this.balanceService.calculateBalance().catch(err => {
          console.error('[AppComponent] Error en carga background del balance:', err);
        });
      })
    );

    // Carga en background de las eSIMs del usuario al autenticarse
    // (tanto login fresco como recarga de página con sesión activa)
    this.subscription.add(
      this.authService.authStatus$.pipe(
        distinctUntilChanged((a, b) => a.loggedIn === b.loggedIn),
        filter(status => status.loggedIn)
      ).subscribe(() => {
        this.globalDataService.setUserEsimsLoading();
        this.esimService.getEsimList()
          .then(list => {
            this.globalDataService.setUserEsims(list ?? []);
          })
          .catch(err => {
            console.error('[AppComponent] Error en carga background de eSIMs del usuario:', err);
            this.globalDataService.invalidateUserEsims();
          });
      })
    );
  }

  ngOnDestroy() {
    // unsubscribe to cookieconsent observables to prevent memory leaks
    this.subscription.unsubscribe();
  }

  /**
   * Refresca en background los datos cacheados que hayan expirado.
   * Se llama en cada navegación y al volver al foco de la pestaña.
   */
  private refreshStaleData(): void {
    if (!this.authService.getUser().loggedIn) return;

    // eSIMs del usuario
    if (this.globalDataService.isUserEsimsStale() && !this.globalDataService.isUserEsimsLoading()) {
      this.globalDataService.setUserEsimsLoading();
      this.esimService.getEsimList()
        .then(list => this.globalDataService.setUserEsims(list ?? []))
        .catch(err => {
          console.error('[AppComponent] Error al refrescar eSIMs del usuario:', err);
          this.globalDataService.invalidateUserEsims();
        });
    }

    // Catálogo eSIM y demás datos estáticos (TTL 1h, gestionado por AppInitService)
    this.appInitService.refreshIfStale();
  }

  isBackofficeRoute(): boolean {
    return this.router.url.startsWith('/backoffice');
  }
}
